import { useCallback, useMemo, useState } from 'react';

import { initialAiBuilderComposerSignalState } from '../consts';
import { type OpenAiBuilderChatParams } from '../stores/types';

type AiBuilderChatComposerSignalState = {
  focusRequestId: number;
  filePickerRequestId: number;
  confirmActionAttentionRequestId: number;
  confirmActionAttentionMessageId: string | null;
};

export const useAiBuilderComposerSignals = () => {
  const [composerSignalState, setComposerSignalState] =
    useState<AiBuilderChatComposerSignalState>(
      initialAiBuilderComposerSignalState,
    );

  const requestComposerFocus = useCallback(() => {
    setComposerSignalState(prev => ({
      ...prev,
      focusRequestId: prev.focusRequestId + 1,
    }));
  }, []);

  const requestConfirmActionAttention = useCallback((messageId: string) => {
    setComposerSignalState(prev => ({
      ...prev,
      confirmActionAttentionRequestId: prev.confirmActionAttentionRequestId + 1,
      confirmActionAttentionMessageId: messageId,
    }));
  }, []);

  const requestComposerEntrySignal = useCallback(
    (entry: OpenAiBuilderChatParams['entry']) => {
      setComposerSignalState(prev => ({
        focusRequestId: prev.focusRequestId + 1,
        filePickerRequestId:
          entry === 'file'
            ? prev.filePickerRequestId + 1
            : prev.filePickerRequestId,
        confirmActionAttentionRequestId: prev.confirmActionAttentionRequestId,
        confirmActionAttentionMessageId: prev.confirmActionAttentionMessageId,
      }));
    },
    [],
  );

  const resetFilePickerRequest = useCallback(() => {
    setComposerSignalState(prev =>
      prev.filePickerRequestId === 0
        ? prev
        : { ...prev, filePickerRequestId: 0 },
    );
  }, []);

  const resetComposerSignalsForOpenPanel = useCallback(() => {
    setComposerSignalState(prev => ({
      focusRequestId: prev.focusRequestId + 1,
      filePickerRequestId: 0,
      confirmActionAttentionRequestId: prev.confirmActionAttentionRequestId,
      confirmActionAttentionMessageId: null,
    }));
  }, []);

  return useMemo(
    () => ({
      composerSignalState,
      requestConfirmActionAttention,
      requestComposerEntrySignal,
      requestComposerFocus,
      resetComposerSignalsForOpenPanel,
      resetFilePickerRequest,
    }),
    [
      composerSignalState,
      requestConfirmActionAttention,
      requestComposerEntrySignal,
      requestComposerFocus,
      resetComposerSignalsForOpenPanel,
      resetFilePickerRequest,
    ],
  );
};
